import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Icon } from 'src/components/Icon';
import { Loader } from 'src/components/Loader';
import { SearchTextField } from 'src/components/SearchTextField';
import { Typography } from 'src/components/Typography';
import { fetchAllFaqs } from 'src/models/contentFaq/contentFaq';

import { FaqCenteredState, FaqWebviewShell } from './FaqWebviewShell';

const rowLinkStyle = {
  color: 'inherit',
  display: 'block',
  textDecoration: 'none',
};

export default function FaqSearchPage() {
  const { t } = useTranslation('translation', { keyPrefix: 'appWebview.faq' });
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');

  const {
    data: faqs = [],
    error,
    isError,
    isLoading,
  } = useQuery({
    queryKey: ['faq-all'],
    queryFn: fetchAllFaqs,
  });

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchValue]);

  const normalizedSearch = debouncedSearchValue.trim().toLowerCase();
  const filteredFaqs = faqs.filter((faq) =>
    faq.title.toLowerCase().includes(normalizedSearch)
  );

  if (isLoading) {
    return (
      <FaqWebviewShell>
        <FaqCenteredState>
          <Loader
            label={t('loadingFaqs', { defaultValue: 'Loading FAQs...' })}
            size="medium"
            sx={{
              minWidth: 'auto',
              width: 'auto',
            }}
          />
        </FaqCenteredState>
      </FaqWebviewShell>
    );
  }

  if (isError) {
    return (
      <FaqWebviewShell>
        <FaqCenteredState>
          <Typography color="error">
            {error instanceof Error
              ? error.message
              : t('searchLoadError', {
                  defaultValue: 'Unable to load FAQs.',
                })}
          </Typography>
        </FaqCenteredState>
      </FaqWebviewShell>
    );
  }

  return (
    <FaqWebviewShell>
      <Box sx={{ p: 2 }}>
        <SearchTextField
          autoFocus
          onCancel={() => setSearchValue('')}
          onSearch={setSearchValue}
          placeholder={t('searchPlaceholder', { defaultValue: 'Search FAQs' })}
          value={searchValue}
          sx={{ bgcolor: 'background.paper' }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
          {filteredFaqs.length ? (
            filteredFaqs.map((faq) => {
              const faqSlug = faq.cachedSlug || String(faq.id);

              return (
                <RouterLink
                  key={String(faq.id)}
                  style={rowLinkStyle}
                  to={`/mobile/faq/content/${faqSlug}`}
                >
                  <Box
                    sx={{
                      alignItems: 'center',
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      display: 'flex',
                      gap: 1.5,
                      px: 1.5,
                      py: 1.25,
                    }}
                  >
                    <Icon color="primary" icon={faCircleQuestion} />
                    <Typography>{faq.title}</Typography>
                  </Box>
                </RouterLink>
              );
            })
          ) : (
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography color="text.secondary">
                {t('notFound', { defaultValue: 'No FAQs found.' })}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </FaqWebviewShell>
  );
}
